import { useEffect, useState } from "react";
import { Navigate } from "react-router";
import { useTranslation } from "react-i18next";
import { getCurrentPortalConsent } from "../../application/portalRecords";
import { usePortalRecords } from "../../application/usePortalAuth";

type Status = "loading" | "ok" | "required" | "noContext" | "failed";

/**
 * Consent is held per record, because each record has its own controller.
 *
 * So this re-checks whenever the active record changes: a patient who
 * consented with one professional and not the other reaches the first panel
 * normally and lands on the consent screen for the second, and only for it.
 */
function ConsentCheckFailed({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <main className="mx-auto flex min-h-screen w-full max-w-md flex-col items-center justify-center gap-4 px-4 text-center">
      <p className="text-sm text-content-tertiary">
        {t("portal.consentCheck.failed", "We could not check your consent just now.")}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-lg bg-brand-500 px-4 py-2 text-sm text-white focus-visible:outline-2 focus-visible:outline-brand-500"
      >
        {t("portal.consentCheck.retry", "Try again")}
      </button>
    </main>
  );
}

export default function RequireCurrentConsent({ children }: { children: React.ReactNode }) {
  const { activePatientId } = usePortalRecords();
  const [status, setStatus] = useState<Status>("loading");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    getCurrentPortalConsent()
      .then((data) => {
        if (!cancelled) setStatus(data.requires_acceptance ? "required" : "ok");
      })
      .catch((error) => {
        if (cancelled) return;
        // Only a real answer about consent sends the patient to the consent
        // screen. A refused or missing record context, and a network failure,
        // used to land there too and trap them on a form for a record they had
        // already consented to.
        const status = error?.response?.status;
        setStatus([400, 403, 404].includes(status) ? "noContext" : "failed");
      });
    return () => {
      cancelled = true;
    };
  }, [activePatientId, attempt]);

  if (status === "loading") return null;
  if (status === "required") return <Navigate to="/portal/consent" replace />;
  // The record could not be resolved: ask which panel, not which form.
  if (status === "noContext") return <Navigate to="/portal/choose" replace />;
  // Something transient. The gate stays closed — consent is not something to
  // wave through on a failed request — but it says so and offers a retry
  // instead of sending them to a form they may not need.
  if (status === "failed") return <ConsentCheckFailed onRetry={() => setAttempt((n) => n + 1)} />;

  return <>{children}</>;
}
